export const ADMIN_TOKEN_KEY = 'plugin-admin-token'
export const ADMIN_USERNAME_KEY = 'plugin-admin-username'

export type Plugin = {
  id: string
  name: string
  author: string
  category: string
  group?: string
  summary: string
  description: string
  runtime: string
  status: 'draft' | 'listed' | 'unlisted'
  billingType: 'free' | 'paid'
  icon?: string
  previews?: string
  downloadCount?: number
  ratingAverage?: number
  ratingCount?: number
  createdAt: number
  updatedAt: number
}

export type PluginVersion = {
  id: number
  pluginId: string
  version: string
  runtime: string
  artifacts: PluginArtifact[]
  frontendPath?: string
  settingsSchemaPath?: string
  status: string
  createdAt: number
  updatedAt: number
}

export type PluginArtifact = {
  id: number
  versionId: number
  os: string
  arch: string
  checksum: string
  sizeBytes: number
  createdAt: number
  updatedAt: number
}

export type Authorization = {
  id: number
  pluginId: string
  scopeType: 'deployment'
  scopeKey: string
  source: 'activation_code' | 'admin_grant' | 'purchase'
  status: 'active' | 'disabled' | 'expired'
  grantedBy?: string
  expiresAt?: number
  createdAt: number
  updatedAt: number
}

export type DeviceAccess = {
  id: number
  deviceId: string
  firstIp: string
  os: string
  osVersion: string
  kernelVersion: string
  arch: string
  appVersion: string
  goVersion: string
  cpuCount: number
  memoryBytes: number
  hostnameHash: string
  userAgent: string
  firstAccess: number
  lastAccess: number
}

export type PluginAuditLog = {
  id: number
  deviceId: string
  pluginId: string
  action: string
  success: boolean
  message: string
  ip: string
  createdAt: number
}

export type ActivationCode = {
  id: number
  pluginId: string
  code: string
  status: 'unused' | 'activated' | 'revoked'
  activatedAccountKey?: string
  redeemedScopeType?: string
  redeemedScopeKey?: string
  activatedAt?: number
  createdBy?: string
  createdAt: number
  updatedAt: number
}

export function getAdminToken() {
  try {
    return localStorage.getItem(ADMIN_TOKEN_KEY) || ''
  } catch {
    return ''
  }
}

export function setAdminToken(token: string) {
  try {
    localStorage.setItem(ADMIN_TOKEN_KEY, token)
  } catch {
    // ignore
  }
}

export function clearAdminToken() {
  try {
    localStorage.removeItem(ADMIN_TOKEN_KEY)
  } catch {
    // ignore
  }
}

export function getAdminUsername() {
  try {
    return localStorage.getItem(ADMIN_USERNAME_KEY) || ''
  } catch {
    return ''
  }
}

export function setAdminUsername(username: string) {
  try {
    localStorage.setItem(ADMIN_USERNAME_KEY, username)
  } catch {
    // ignore
  }
}

export async function login(username: string, password: string): Promise<{success: boolean; token: string; username: string}> {
  const response = await fetch('/api/auth/login', {
    method: 'POST',
    headers: {'Content-Type': 'application/json', Accept: 'application/json'},
    body: JSON.stringify({username, password}),
  })
  return parseResponse(response)
}

export async function changePassword(oldPassword: string, newPassword: string): Promise<{success: boolean; username: string}> {
  return apiPost('/auth/change-password', {oldPassword, newPassword})
}

export async function apiGet<T>(path: string): Promise<T> {
  return request<T>('GET', path)
}

export async function apiPost<T>(path: string, body?: unknown): Promise<T> {
  return request<T>('POST', path, body)
}

export async function apiPatch<T>(path: string, body?: unknown): Promise<T> {
  return request<T>('PATCH', path, body)
}

export async function apiDelete<T>(path: string): Promise<T> {
  return request<T>('DELETE', path)
}

export async function apiUpload<T>(path: string, file: File): Promise<T> {
  const form = new FormData()
  form.append('file', file)
  const response = await fetch(`/api${path}`, {
    method: 'POST',
    headers: authHeaders(false),
    body: form,
  })
  return parseResponse<T>(response)
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const response = await fetch(`/api${path}`, {
    method,
    headers: authHeaders(body !== undefined),
    body: body === undefined ? undefined : JSON.stringify(body),
    cache: method === 'GET' ? 'no-store' : undefined,
  })
  return parseResponse<T>(response)
}

function authHeaders(json: boolean) {
  const headers: Record<string, string> = {Accept: 'application/json'}
  const token = getAdminToken()
  if (token) headers.Authorization = `Bearer ${token}`
  if (json) headers['Content-Type'] = 'application/json'
  return headers
}

async function parseResponse<T>(response: Response): Promise<T> {
  const text = await response.text()
  const body = text ? JSON.parse(text) : {}
  if (!response.ok || body.success === false) {
    throw new Error(body.message || `HTTP ${response.status}`)
  }
  return body as T
}
